import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pad2 = (n) => String(n).padStart(2, "0");
const utcDayStr = (d) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;

const presetRange = (p) => {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const localDay = (offset) => {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - offset);
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  };
  if (p === "today") return { from: localDay(0), to: localDay(0) };
  if (p === "yesterday") return { from: localDay(1), to: localDay(1) };
  if (p === "three_days_ago") return { from: localDay(3), to: localDay(3) };
  if (p === "this_week") {
    const dow = (now.getDay() + 6) % 7;
    const mon = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dow);
    return { from: `${mon.getFullYear()}-${pad2(mon.getMonth() + 1)}-${pad2(mon.getDate())}`, to: localDay(0) };
  }
  if (p === "this_month") return { from: utcDayStr(new Date(Date.UTC(y, m, 1))), to: "" };
  if (p === "last_month") return { from: utcDayStr(new Date(Date.UTC(y, m - 1, 1))), to: utcDayStr(new Date(Date.UTC(y, m, 0))) };
  if (p === "last_30") return { from: utcDayStr(new Date(now.getTime() - 29 * 86400000)), to: "" };
  if (p === "this_year") return { from: `${y}-01-01`, to: "" };
  return { from: "", to: "" };
};

const PRESETS = [
  ["all", "All time"], ["this_week", "This week"], ["this_month", "This month"],
  ["last_month", "Last month"], ["last_30", "Last 30 days"], ["this_year", "This year"],
  ["today", "Today"], ["yesterday", "Yesterday"], ["three_days_ago", "3 days ago"],
];

const BUCKET_LABELS = {
  BOOK: "Book",
  WEBSITE: "Website",
  GOOGLE: "Google",
  EMAIL: "Email",
  OTHER: "Other",
  DIRECT: "Direct",
};
const BUCKET_HINTS = {
  BOOK: "Arrived through one of the five in-book redirects",
  WEBSITE: "Referrer was thebiologytrader.com",
  GOOGLE: "Referrer was a Google search domain",
  EMAIL: "Arrived carrying utm_source=email",
  OTHER: "Another external referrer",
  DIRECT: "No readable referrer and no UTM",
};

const fmtUtcDay = (d) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });

const fmtWeek = (d) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });

// Lightweight inline sparkline (no chart dependency) for the weekly unique-clicks trend.
const UniqueTrend = ({ weeks }) => {
  const w = 240, h = 44, pad = 4;
  const max = Math.max(1, ...weeks.map((p) => p.unique));
  const n = weeks.length;
  const x = (i) => pad + (n <= 1 ? 0 : (i * (w - 2 * pad)) / (n - 1));
  const y = (v) => h - pad - (v / max) * (h - 2 * pad);
  const d = weeks.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.unique).toFixed(1)}`).join(" ");
  const last = weeks[weeks.length - 1];
  const prev = weeks[weeks.length - 2];
  return (
    <div className="mt-4 mb-6 border border-hairline p-4 max-w-md" data-testid="cage-unique-trend">
      <div className="flex items-center justify-between mb-2">
        <span className="label-mono text-muted">Weekly approx. unique clicks · last 8 weeks</span>
        <span className="label-mono text-ink" data-testid="cage-unique-trend-latest">
          {last ? last.unique : 0} this week
          {prev && last ? (
            <span className={last.unique >= prev.unique ? "text-green-700 ml-2" : "text-red-600 ml-2"}>
              {last.unique - prev.unique >= 0 ? "+" : ""}{last.unique - prev.unique} vs last
            </span>
          ) : null}
        </span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-11" preserveAspectRatio="none" data-testid="cage-unique-trend-spark">
        <path d={d} fill="none" stroke="#DC2626" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
        {weeks.map((p, i) => (
          <circle key={i} cx={x(i)} cy={y(p.unique)} r="1.8" fill="#DC2626" />
        ))}
      </svg>
      <div className="flex justify-between mt-1">
        <span className="label-mono text-muted text-xs">{weeks[0] ? fmtWeek(weeks[0].week_start) : ""}</span>
        <span className="label-mono text-muted text-xs">{last ? fmtWeek(last.week_start) : ""}</span>
      </div>
    </div>
  );
};

export default function AdminCageSystem() {
  const [authed, setAuthed] = useState(null);
  const [pw, setPw] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [preset, setPreset] = useState("all");

  const invalid = Boolean(from && to && from > to);
  const rFrom = invalid ? "" : from;
  const rTo = invalid ? "" : to;
  const rangeActive = Boolean(rFrom || rTo);
  const rangeLabel = !rangeActive
    ? "All time"
    : rFrom && rTo
      ? `${fmtUtcDay(rFrom)} – ${fmtUtcDay(rTo)}`
      : rFrom
        ? `From ${fmtUtcDay(rFrom)}`
        : `Through ${fmtUtcDay(rTo)}`;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (!invalid) {
        if (from) params.date_from = from;
        if (to) params.date_to = to;
      }
      const res = await axios.get(`${API}/admin/cage-system`, { params, withCredentials: true });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        setAuthed(false);
        return;
      }
      setError("Could not load. Log out and back in, then try again.");
    } finally {
      setLoading(false);
    }
  }, [from, to, invalid]);

  useEffect(() => {
    (async () => {
      try {
        await axios.get(`${API}/admin/me`, { withCredentials: true });
        setAuthed(true);
      } catch {
        setAuthed(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (authed) fetchData();
  }, [authed, fetchData]);

  const login = async (e) => {
    e.preventDefault();
    if (!pw) return;
    setLoginLoading(true);
    setLoginError("");
    try {
      await axios.post(`${API}/admin/login`, { password: pw }, { withCredentials: true });
      setAuthed(true);
      setPw("");
    } catch (err) {
      setLoginError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Something went wrong.");
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    setAuthed(false);
    setData(null);
  };

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper" data-testid="admin-cage-loading">
        <p className="text-muted">Loading…</p>
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper px-6" data-testid="admin-cage-login">
        <form onSubmit={login} className="w-full max-w-sm">
          <div className="label-mono text-muted">Cage System admin</div>
          <h1 className="font-display text-4xl md:text-5xl mt-6 mb-10 leading-tight">Enter password.</h1>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Password"
            className="w-full bg-transparent border-b border-ink py-3 text-lg outline-none"
            data-testid="admin-cage-password-input"
            autoFocus
          />
          {loginError && (
            <p className="mt-4 text-sm text-red-600" data-testid="admin-cage-login-error">{loginError}</p>
          )}
          <button
            type="submit"
            disabled={loginLoading}
            className="mt-8 w-full bg-ink text-white py-4 label-mono hover:bg-leaf transition-colors disabled:opacity-60"
            data-testid="admin-cage-login-submit"
          >
            {loginLoading ? "Checking…" : "Enter"}
          </button>
        </form>
      </div>
    );
  }

  const s1 = data?.section1;
  const s2 = data?.section2;
  const s3 = data?.section3;
  const otherByHost = {};
  (s3?.other_hosts || []).forEach((h) => { otherByHost[h.host] = h.count; });

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-cage-dashboard">
      <header className="border-b border-hairline sticky top-0 z-10 backdrop-blur-md bg-white/85">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <span className="font-display text-lg whitespace-nowrap" style={{ fontWeight: 800 }}>
            Cage System
          </span>
          <button
            onClick={logout}
            className="label-mono text-muted hover:text-ink transition-colors ml-4"
            data-testid="admin-cage-logout"
          >
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <h2 className="font-display text-3xl md:text-4xl mb-8">Cage System</h2>

        {/* Date range control (matches Dashboard) */}
        <div className="mb-10 border border-hairline p-4" data-testid="cage-range-control">
          <div className="flex flex-wrap items-end gap-4">
            <label className="label-mono text-muted flex items-center gap-2">
              From
              <input
                type="date"
                value={from}
                onChange={(e) => { setFrom(e.target.value); setPreset("custom"); }}
                className="bg-transparent border-b border-ink py-1 outline-none text-ink"
                data-testid="cage-range-from"
              />
            </label>
            <label className="label-mono text-muted flex items-center gap-2">
              To
              <input
                type="date"
                value={to}
                onChange={(e) => { setTo(e.target.value); setPreset("custom"); }}
                className="bg-transparent border-b border-ink py-1 outline-none text-ink"
                data-testid="cage-range-to"
              />
            </label>
            <div className="flex flex-wrap gap-1">
              {PRESETS.map(([p, lbl]) => (
                <button
                  key={p}
                  onClick={() => { const r = presetRange(p); setFrom(r.from); setTo(r.to); setPreset(p); }}
                  className={`label-mono px-3 py-1.5 border transition-colors ${preset === p ? "bg-ink text-white border-ink" : "border-hairline text-ink hover:bg-surface"}`}
                  data-testid={`cage-preset-${p}`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          {invalid ? (
            <p className="mt-3 text-sm text-red-600" data-testid="cage-range-invalid">
              FROM is later than TO — fix the dates. Showing all time until then.
            </p>
          ) : (
            <p className="mt-3 label-mono text-muted" data-testid="cage-range-active">Showing: {rangeLabel}</p>
          )}
        </div>

        {error && <p className="mb-6 text-sm text-red-600" data-testid="admin-cage-error">{error}</p>}
        {loading && !data && <p className="text-muted" data-testid="admin-cage-loading-data">Loading…</p>}

        {data && (
          <div className={`space-y-14 ${loading ? "opacity-60" : ""}`}>
            {/* SECTION 1 — IN-BOOK LINKS */}
            <section data-testid="cage-section-1">
              <h3 className="label-mono text-muted mb-1">In-book links</h3>
              <p className="text-sm text-muted mb-4">
                Clicks on each printed redirect path. Bots excluded. Approximate unique clicks count distinct visitors (salted hash of IP + user agent).
                {" "}An asterisk (*) marks a figure counted only from clicks recorded since this feature shipped — earlier clicks carry no hash and are not included, so the unique count is partial (never guessed).
              </p>
              {s1?.unique_trend?.ready ? (
                <UniqueTrend weeks={s1.unique_trend.weeks} />
              ) : (
                <p className="mt-3 mb-6 label-mono text-muted" data-testid="cage-unique-trend-pending">
                  Weekly unique-click trend builds up as clicks are recorded (needs at least two full past weeks of data).
                </p>
              )}
              <div className="border-t border-hairline divide-y divide-hairline">
                {(s1?.rows || []).map((r) => (
                  <div key={r.slug} className="py-4 grid grid-cols-12 gap-4 items-center" data-testid={`cage-link-row-${r.slug}`}>
                    <div className="col-span-12 md:col-span-7">
                      <div className="font-display text-lg leading-tight">{r.label}</div>
                      <div className="label-mono text-muted mt-0.5">{r.path}</div>
                    </div>
                    <div className="col-span-6 md:col-span-3">
                      <div className="label-mono text-muted">Total clicks</div>
                      <div className="font-display text-2xl" data-testid={`cage-link-total-${r.slug}`}>{r.total_clicks}</div>
                    </div>
                    <div className="col-span-6 md:col-span-2">
                      <div className="label-mono text-muted">Approx. unique</div>
                      <div className="font-display text-2xl" data-testid={`cage-link-unique-${r.slug}`}>
                        {r.unique_clicks == null ? "—" : r.unique_clicks}
                        {r.unique_partial && (
                          <span className="text-red-600" title="Partial — excludes clicks recorded before this feature shipped" data-testid={`cage-link-unique-partial-${r.slug}`}>*</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* SECTION 2 — CAGE SYSTEM PAGE VISITS */}
            <section data-testid="cage-section-2">
              <h3 className="label-mono text-muted mb-4">Cage System page visits</h3>
              <div className="grid grid-cols-2 gap-6 max-w-md">
                <div className="border border-hairline p-5">
                  <div className="label-mono text-muted">Total visits to /cage-system</div>
                  <div className="font-display text-4xl mt-1" data-testid="cage-total-visits">{s2?.total_visits ?? 0}</div>
                </div>
                <div className="border border-hairline p-5">
                  <div className="label-mono text-muted">Unique visits (per 30-min session)</div>
                  <div className="font-display text-4xl mt-1" data-testid="cage-unique-visits">{s2?.unique_visits ?? 0}</div>
                </div>
              </div>
            </section>

            {/* SECTION 3 — WHERE VISITS CAME FROM */}
            <section data-testid="cage-section-3">
              <h3 className="label-mono text-muted mb-4">Where Cage System visits came from</h3>
              <div className="border-t border-hairline divide-y divide-hairline">
                {(s3?.buckets || []).map((b) => (
                  <div key={b.bucket} data-testid={`cage-bucket-${b.bucket}`}>
                    <div className="py-4 grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-8 md:col-span-9">
                        <div className="font-display text-lg leading-tight">{BUCKET_LABELS[b.bucket] || b.bucket}</div>
                        <div className="label-mono text-muted mt-0.5">{BUCKET_HINTS[b.bucket]}</div>
                      </div>
                      <div className="col-span-4 md:col-span-3 text-right md:text-left">
                        <div className="font-display text-2xl" data-testid={`cage-bucket-count-${b.bucket}`}>{b.count}</div>
                      </div>
                    </div>
                    {b.bucket === "OTHER" && (s3?.other_hosts || []).length > 0 && (
                      <div className="pb-4 pl-4 md:pl-6">
                        <div className="border-l border-hairline pl-4 space-y-1">
                          {(s3?.other_hosts || []).map((h) => (
                            <div key={h.host} className="flex items-center justify-between max-w-md" data-testid={`cage-other-host-${h.host}`}>
                              <span className="text-sm text-ink break-all">{h.host}</span>
                              <span className="label-mono text-muted">{h.count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
